import { useEffect, useRef, useCallback } from 'react';

import mapboxgl from 'mapbox-gl';

import { updatePathSource, flyToPosition, jumpToPosition } from './mapOperations';
import { LiveLineData, MarkerManager } from './mapTypes';
import { calculateGimbalOffset } from './mapUtils';
import { updateDroneMarker, updateGimbalMarker } from './markerOperations';

export function useLiveLineUpdates(
  map: React.RefObject<mapboxgl.Map | null>,
  markerManagerRef: React.RefObject<MarkerManager>,
  liveLine: LiveLineData | undefined,
  mapLoaded: boolean,
  inPipMode: boolean,
  targetZoomRef: React.MutableRefObject<number>,
  createdTimeRef: React.MutableRefObject<number>,
  flyToEnabledRef: React.MutableRefObject<boolean>
) {
  const lastProcessedLengthRef = useRef<number>(0);
  const animationFrameRef = useRef<number | null>(null);
  const hasInitiallyPositionedRef = useRef<boolean>(false);

  const updateDrone = useCallback(
    (
      deviceId: string,
      position: [number, number],
      markerImage: HTMLImageElement | null,
      markerAngle: number | undefined,
      lines: [number, number][]
    ) => {
      if (!map.current || !markerManagerRef.current) return;
      updateDroneMarker(map.current, markerManagerRef.current, deviceId, position, markerImage, markerAngle, lines);
    },
    [map, markerManagerRef]
  );

  const updateGimbal = useCallback(
    (
      deviceId: string,
      dronePosition: [number, number],
      gimbalImage: HTMLImageElement | null,
      gimbalAngle: number | undefined,
      lines: [number, number][]
    ) => {
      if (!map.current || !markerManagerRef.current) return;
      updateGimbalMarker(
        map.current,
        markerManagerRef.current,
        deviceId,
        dronePosition,
        gimbalImage,
        gimbalAngle,
        lines,
        inPipMode
      );
    },
    [map, markerManagerRef, inPipMode]
  );

  const updatePath = useCallback(
    (coordinates: [number, number][]) => {
      updatePathSource(map.current, coordinates, inPipMode);
    },
    [map, inPipMode]
  );

  const flyTo = useCallback(
    (target: [number, number], zoom?: number) => {
      if (inPipMode && !hasInitiallyPositionedRef.current) {
        jumpToPosition(map.current, target, targetZoomRef, createdTimeRef, zoom);
        hasInitiallyPositionedRef.current = true;
      } else {
        flyToPosition(map.current, target, targetZoomRef, createdTimeRef, zoom, flyToEnabledRef.current);
      }
    },
    [map, targetZoomRef, createdTimeRef, flyToEnabledRef, inPipMode]
  );

  useEffect(() => {
    hasInitiallyPositionedRef.current = false;
  }, [mapLoaded, inPipMode]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !liveLine) {
      return;
    }

    const updateMap = () => {
      if (!map.current || !liveLine || liveLine.lines.length === 0) {
        animationFrameRef.current = requestAnimationFrame(updateMap);
        return;
      }

      const { deviceId, lines, marker, markerAngle, gimbal, gimbalAngle, canUpdateMarkerLocation } = liveLine;
      const currentPosition = lines[lines.length - 1];

      if (canUpdateMarkerLocation) {
        updateDrone(deviceId, currentPosition, marker || null, markerAngle, lines);
        updateGimbal(deviceId, currentPosition, gimbal || null, gimbalAngle, lines);
      }

      const currentLength = lines.length;
      if (currentLength > lastProcessedLengthRef.current) {
        lastProcessedLengthRef.current = currentLength;
        updatePath(lines);

        if (markerManagerRef.current) {
          const droneMarker = markerManagerRef.current.getMarker(deviceId);
          if (droneMarker && canUpdateMarkerLocation) {
            flyTo(currentPosition);
          }
        }
      }

      animationFrameRef.current = requestAnimationFrame(updateMap);
    };

    animationFrameRef.current = requestAnimationFrame(updateMap);

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [liveLine, mapLoaded, updateDrone, updateGimbal, updatePath, flyTo, markerManagerRef]);

  useEffect(() => {
    if (!liveLine?.deviceId || !markerManagerRef.current) return;

    const manager = markerManagerRef.current;
    const markerKey = liveLine.deviceId;
    const gimbalMarkerId = `${liveLine.deviceId}_gimbal`;

    const existingMarker = manager.getMarker(markerKey);
    const existingGimbalMarker = manager.getMarker(gimbalMarkerId);

    if (
      (existingMarker && !manager.created.has(markerKey)) ||
      (existingGimbalMarker && !manager.created.has(gimbalMarkerId))
    ) {
      if (existingMarker) manager.removeMarker(markerKey);
      if (existingGimbalMarker) manager.removeMarker(gimbalMarkerId);
    }
  }, [liveLine?.deviceId, markerManagerRef]);

  useEffect(() => {
    if (!map.current || !mapLoaded || !liveLine) return;

    const updateGimbalPosition = () => {
      if (!map.current || !markerManagerRef.current) return;

      const gimbalMarkerId = `${liveLine.deviceId}_gimbal`;
      const gimbalMarker = markerManagerRef.current.getMarker(gimbalMarkerId);

      if (gimbalMarker && liveLine.lines.length > 0 && liveLine.gimbalAngle !== undefined) {
        const dronePos = liveLine.lines[liveLine.lines.length - 1];
        const gimbalPos = calculateGimbalOffset(dronePos, liveLine.gimbalAngle, map.current, inPipMode);
        markerManagerRef.current.updateMarker(gimbalMarkerId, gimbalPos);
      }
    };

    map.current.on('move', updateGimbalPosition);
    map.current.on('zoom', updateGimbalPosition);

    return () => {
      map.current?.off('move', updateGimbalPosition);
      map.current?.off('zoom', updateGimbalPosition);
    };
  }, [liveLine, mapLoaded, inPipMode, map, markerManagerRef]);
}
